import api from '@wochitnpm/multiloginapi';
import app from 'app';

export default class MultiloginAdapter {
  constructor(next) {
    this.allowAccess = next;

    if (!this.shouldCheckAccess()) {
      this.allowAccess();
      return;
    }

    // events that may happen during checking access (one of them)
    api.once(api.OUT_EVENT.SELF_IS_SEATING, this.onSelfIsSeating.bind(this));
    api.once(api.OUT_EVENT.SELF_CAN_SEAT, this.onSelfCanSeat.bind(this));
    api.once(api.OUT_EVENT.SELF_CAN_KICK, this.onSelfCanKick.bind(this));
    api.once(
      api.OUT_EVENT.SELF_KICKED_AFTER_REENTER,
      this.onSelfKickedAfterReenter.bind(this)
    );

    // events that may happen after access was granted
    api.once(
      api.OUT_EVENT.SELF_KICKED_DURING_WORK,
      this.onSelfKickedDuringWork.bind(this)
    );
    api.once(
      api.OUT_EVENT.SELF_SUSPECTED_IN_TAMPERING,
      this.onSuspectedInTampering.bind(this)
    );

    app.remoteStash.whenReady.then(({ fbdb, userId }) => {
      app.rootStore.watch(
        () => {
          return app.rootStore.state.UserSession_isActive;
        },
        (sessionIsActive) => {
          api.emit(api.IN_EVENT.ACTIVITY_CHANGED, sessionIsActive);
        }
      );

      api
        .setContext({
          db: fbdb,
          userId,
          applicationName: 'studio',
          // hardcoded number of seats allowed per account
          defaultNumberOfSeats: 1,
          isProd: app.rootStore.getters['user/box'].isProd(),
        })
        .then(
          () => {
            api.checkAccess().catch((bug) => {
              // allow access to avert disastrous consequences of a bug in multilogin api
              this.allowAccess();
              app.doctorController.report('multilogin api checkAccess', bug);
            });
          },
          (bug) => {
            this.allowAccess();
            app.doctorController.report('multilogin api setContext', bug);
          }
        );

      const link = `https://wochit-fe-remote-stash-test.firebaseio.com/users/${userId}/multilogin-studio`;
      if (!app.rootStore.getters['user/box'].isProd()) {
        console.log('CID', api.cid, link);
      }
    });
  }

  shouldCheckAccess() {
    const override = false;
    const environment = !app.rootStore.getters['user/box'].isDev();
    const appMode = !app.isEmbedded;
    const userType =
      !app.rootStore.getters['user/type'].ACCOUNT_MANAGER &&
      !app.rootStore.getters['user/type'].WOCHIT_INTERNAL &&
      !app.rootStore.getters['user/type'].WOCHIT_OPS;

    return override || (environment && appMode && userType);
  }

  onSelfIsSeating(e, cbAcknowledge) {
    cbAcknowledge();
    this.allowAccess();
  }

  onSelfCanSeat(e, cbAcknowledge) {
    app.analytics('multilogin:foundSeatUnused');
    cbAcknowledge();
    this.allowAccess();
  }

  onSelfCanKick(e, cbKick, cbAbort) {
    this.promptToKick().then(
      () => {
        app.analytics('multilogin:foundSeatTaken', {
          Reaction: 'Took the seat',
        });
        cbKick();
        this.allowAccess();
      },
      () => {
        app.analytics('multilogin:foundSeatTaken', {
          Reaction: 'Leaved the seat',
        });
        cbAbort();
        app.rootStore.dispatch('user/logout', { soft: true });
        app.state.enableNavigation();
        app.navigate2Login();
      }
    );
  }

  onSelfKickedDuringWork(e, cbAcknowledge) {
    app.analytics('multilogin:kickedFromSeat', { When: 'During work' });
    cbAcknowledge();
    app.rootStore.dispatch('user/logout', { soft: true });
    app.connectionController.userWasKicked();
  }

  onSelfKickedAfterReenter(e, cbAcknowledge) {
    app.analytics('multilogin:kickedFromSeat', { When: 'At page reload' });
    cbAcknowledge();
    app.rootStore.dispatch('user/logout', { soft: true });
    app.connectionController.userWasKicked();
  }

  onSuspectedInTampering(e) {
    app.analytics('multilogin:tampering');
  }

  promptToKick() {
    return new Promise((resolve, reject) => {
      app.delayedSpinner.hide();
      app.dialog
        .error({
          type: app.dialog.types.ERROR_3,
          title:
            'Someone else is in your seat. \u00a0\u00a0\u00a0\u00a0\u00a0 Log them off?',
          description: `
          Only one person can use the seat <i>${app.rootStore.state.user.username}</i> at a time.
          If you continue to log in, the other user will be logged out.
          <br/><br/>
        `.normalizeSpace(),
          buttonYesText: 'Yes, Take Over Seat',
          showContactCsm: true,
          csmSentenceText: 'Need another seat?',
          csmLinkText: 'Contact our Support',
          csmChatMessage:
            "Hi,\nI'd like to ask about adding more user seats to my account.\n",
        })
        .then((res) => {
          if (res.action) {
            resolve();
          } else {
            reject();
          }
        });
    });
  }
}
